import { useCallback, useEffect, useRef, useState } from 'react';
import type { AssetReviewItem, BuildAssetReviewRecoveryStatus } from '@volley/crucible-ipc-types';
import type { ProjectPlacement } from '@volley/crucible/runtime/agent-runtime-profile.js';
import { currentStageView } from './asset-stage-view.js';
import {
  advanceFocusAfterApproval,
  carouselCards,
  reconcileFocus,
  stepFocus,
  type StepDirection,
} from './carousel-focus.js';
import { PhoneSteeringController, type ApproveAllState } from './PhoneSteeringController.js';
import {
  type MoodboardCandidate,
  type MoodboardPickStage,
  type OwnedAssetIds,
  type SteeringSnapshot,
  candidateImageUrl,
  isPickOneStage,
  ownedAssetIdsFromCommandResult,
  reconcileSteeringSnapshot,
} from './tv-steering-types.js';
import { useCrucibleClient } from '../transport/context.js';
import { useFeatureFlags } from '../hooks/useFeatureFlags.js';

/**
 * #2058 (Phase 2b · stream 2b) — THE TV-PREVIEW STEERING SURFACE.
 *
 * Sean's surface model: the art / asset approval lives OFF the chat entirely.
 * The big TV-preview pane — which normally shows the running game — is REPURPOSED
 * during the build (game not built yet) to host the art-steering UI, then flips
 * back to the live game preview once the game is built. There is ONE steering
 * surface, not a chat card competing with the build agent's scrolling commentary.
 *
 * What it shows (staged, style-anchor-first, same backend gate as the chat card):
 *   - PICK-ONE stage → choose 1 of N candidates side-by-side. The user picks one
 *     → renderer sends the pick via `art:command` (the same channel every
 *     stage uses); the others visibly drop.
 *   - EVERY OTHER stage → per-asset YES / NO (+ feedback-regen), via the shared
 *     `currentStageView` projection (`asset-stage-view.ts`) + the `art:*`
 *     invoke channels, re-laid-out for the wide pane.
 *
 * This surface OWNS the snapshot poll (`art:snapshot`) and fires `onDone` once
 * the engine reports `done`. It is mounted by `Workspace` as a full-cover
 * overlay on the preview pane while a review is active; the chat shows only a
 * small "action needed on the TV →" nudge (NOT the approval UI — it is not
 * mirrored).
 *
 * crucible#3112 — rewired off the legacy `asset-review:snapshot`/
 * `asset-review:command` INVOKE calls onto their cloud-reachable `art:*`
 * twins (mirrors the same rewiring #3007 did for `AssetReviewPanel` /
 * `ArtGuidedFlow` / `ArtAssetInspector` / `ArtWorkspace` /
 * `useAssetReviewSurface`; this was the one consumer that PR left out). The
 * `asset-review:ready` / `asset-review:closed` / `asset-review:unreachable`
 * PUSH subscriptions below are unchanged — those are notification channels,
 * not JSON-RPC invokes, and both the desktop main process and the cloud
 * runtime still emit them under the legacy names.
 *
 * Tested by `TvSteeringSurface.journeys.test.tsx`, which mounts this component and
 * drives it through the phone controls, asserting the art:* channel + payload it
 * sends (including that the wrong channel is NOT called). `TvSteeringSurface.test.tsx`
 * holds only the pure helpers and the layout/copy pins alongside it. An earlier
 * version of this note claimed the surface was "Tested SSR-only (the package is
 * node-env)" via stand-ins mirroring this JSX — see crucible#3804.
 */

/** Poll cadence while the surface is mounted (ms) — matches the chat card. */
const POLL_INTERVAL_MS = 1500;
/** Consecutive first-load poll failures tolerated (with a retrying spinner)
 *  before the surface surfaces a hard error. ~POLL_INTERVAL_MS apart. */
const MAX_FIRST_LOAD_RETRIES = 5;

function blocksStaleAssetReviewSnapshot(status: BuildAssetReviewRecoveryStatus): boolean {
  return (
    status.state === 'starting' ||
    status.state === 'stalled' ||
    status.state === 'failed' ||
    status.state === 'stale'
  );
}

/**
 * #2865 — the checkerboard indicating a letterboxed/transparent margin around
 * a reviewed asset's thumbnail. Deliberately applied to `SteeringAssetCard`'s
 * STATIC thumbnail wrapper, never to the `<img>` that carries the
 * `tv-steering-reveal-in` mount animation: that animation's `filter: blur()`
 * blurs the whole element it's on, including any background painted on it, so
 * a checkerboard living on the animated `<img>` would be smeared into a
 * vignette for the ~700ms reveal window and then "pop" into focus — an
 * artifact easily mistaken for the asset's background being removed on
 * approve/steer. See the wrapper `<div>` in `SteeringAssetCard` below.
 */
const TRANSPARENCY_CHECKERBOARD_STYLE: React.CSSProperties = {
  backgroundColor: 'rgba(255,255,255,0.035)',
  backgroundImage:
    'linear-gradient(45deg,rgba(255,255,255,0.055) 25%,transparent 25%),linear-gradient(-45deg,rgba(255,255,255,0.055) 25%,transparent 25%),linear-gradient(45deg,transparent 75%,rgba(255,255,255,0.055) 75%),linear-gradient(-45deg,transparent 75%,rgba(255,255,255,0.055) 75%)',
  backgroundSize: '12px 12px',
  backgroundPosition: '0 0,0 6px,6px -6px,-6px 0',
};

// crucible#3187 — the SpeechRecognition types + `useSpeechFeedback` hook that
// used to live here (private to the old `PhoneSteeringDriver`) moved to
// `../hooks/useSpeechFeedback.ts`, since the new `PhoneSteeringController`
// (a SEPARATE file this component now imports) needs the same hook — see
// that file's docblock for why it isn't imported from here instead
// (circular module dependency).

function candidateLetter(index: number): string {
  return String.fromCharCode(97 + index);
}

function candidateDisplayLabel(index: number): string {
  return `Look ${candidateLetter(index).toUpperCase()}`;
}

function titleCaseWords(value: string): string {
  return value
    .split(/[\s_-]+/g)
    .filter(Boolean)
    .filter((word) => !['keyframe', 'sprite', 'portrait', 'image', 'asset'].includes(word))
    .map((word) => `${word.slice(0, 1).toUpperCase()}${word.slice(1)}`)
    .join(' ');
}

function assetCategoryLabel(category: string): string {
  switch (category) {
    case 'audio':
      return 'Sound';
    case 'background':
      return 'Background';
    case 'portrait':
    case 'sprite':
      return 'Character';
    case 'prop':
      return 'Prop';
    case 'ui':
    case 'shape':
      return 'Interface';
    case 'moodboard':
      return 'Style';
    default:
      return 'Game art';
  }
}

function assetDisplayLabel(asset: AssetReviewItem): string {
  return titleCaseWords(asset.id) || assetCategoryLabel(asset.category);
}

function twoWordDescriptor(value: string): string {
  return value.split(/\s+/g).filter(Boolean).slice(0, 2).join(' ');
}

function assetTvDescriptor(asset: AssetReviewItem): string {
  return twoWordDescriptor(assetDisplayLabel(asset)) || assetCategoryLabel(asset.category);
}

function assetTvStatusLabel(
  asset: AssetReviewItem,
  busy: boolean,
  pendingApprove: boolean,
): string {
  // crucible#2783 — `approved` (the confirmed terminal state) always wins;
  // then a still-unconfirmed approve reads as "Saving" (distinct from a
  // regenerate's "Tweaking"), so a slow approve looks like it's working.
  if (asset.status === 'approved') return 'Kept';
  if (pendingApprove) return 'Saving';
  if (busy) return 'Tweaking';
  if (asset.status === 'staging' || asset.status === 'rejected') return 'Ready';
  return 'Generating';
}

// ── Mount contract ─────────────────────────────────────────────────────────────

export interface TvSteeringSurfaceProps {
  /** kebab-case game id — header sub-line + the `art:command` payload. */
  gameId: string;
  /** Durable asset-review recovery markers exist only for Local workspaces. */
  placement?: ProjectPlacement;
  /**
   * Shows the consumer couch companion: TV renders the build, phone drives the
   * voice-first shaping input. Prosumer workspace uses this as a preview of the
   * consumer experience while chat and Design stay available outside it.
   */
  showPhoneDriverPreview?: boolean;
  /**
   * Lets Workspace mount the TV steering view and phone driver inside the
   * existing PreviewOverlay TV/mobile frame instead of drawing a separate cover.
   */
  renderConsumerFrame?: (parts: {
    tv: React.ReactNode;
    phone: React.ReactNode;
  }) => React.JSX.Element;
  /** Fires exactly once when the staged review reports terminal `done`. */
  onDone?: (snapshot: SteeringSnapshot) => void;
  /** Leaves a failed recovery surface for the workspace's full Art tools. */
  onOpenArt?: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function NorthStarConsumerSurface({
  gameId,
  placement = 'local',
  onDone,
  onOpenArt,
  renderConsumerFrame,
}: {
  gameId: string;
  placement?: ProjectPlacement;
  onDone?: (snapshot: SteeringSnapshot) => void;
  onOpenArt?: () => void;
  renderConsumerFrame?: TvSteeringSurfaceProps['renderConsumerFrame'];
}): React.JSX.Element {
  return (
    <TvSteeringSurface
      gameId={gameId}
      placement={placement}
      showPhoneDriverPreview
      renderConsumerFrame={renderConsumerFrame}
      onDone={onDone}
      onOpenArt={onOpenArt}
    />
  );
}

export function TvSteeringSurface({
  gameId,
  placement = 'local',
  showPhoneDriverPreview = true,
  renderConsumerFrame,
  onDone,
  onOpenArt,
}: TvSteeringSurfaceProps): React.JSX.Element {
  const client = useCrucibleClient();
  // crucible#4051 — defense-in-depth, NOT the primary fix (that's
  // Workspace.tsx's useAssetReviewSurface(gameId, flags.intake &&
  // flags.buildTimeAssetReview, ...) call site, which keeps this component
  // from mounting at all when the flag is off). This read is insurance
  // against `reconcileMoodboardAnchor`'s unconditional collapse (crucible
  // #2740) ever regressing and a genuine mode:'pick-one' stage reaching a
  // MOUNTED surface through some future third render call site. Scoped to
  // exactly what a pick-one stage adds (the candidate grid + the pick-one
  // phone degrade layout) — NOT a blanket suppression of this component,
  // which would also hide the ordinary approve-each carousel + phone driver
  // that mount here for a legitimate ON-flag build-time review.
  const { buildTimeAssetReview: buildTimeAssetReviewEnabled } = useFeatureFlags();
  const [snapshot, setSnapshot] = useState<SteeringSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recoveryStatus, setRecoveryStatus] = useState<BuildAssetReviewRecoveryStatus | null>(null);
  const [retryingRecovery, setRetryingRecovery] = useState(false);
  // crucible#3361 — in-flight tracking is now TWO independent pieces of state
  // instead of one mutually-exclusive value, so multiple cards can regenerate
  // CONCURRENTLY (Keep it on card A no longer blocks giving feedback on card
  // B while A is still cooking — the vendored asset-forge engine has no
  // one-at-a-time lock on distinct assets; see the issue's backend-safety
  // investigation). `inFlightIds` is the set of asset ids with a command
  // outstanding (crucible#3187 — Keep it and the composer both carry a
  // `targetId`, so the common case no longer needs to guess the whole stage
  // is affected the way the pre-targetId ambiguous free-text path did; see
  // regex-not-for-semantic-classification for why THAT guess was a smell).
  // `stageInFlight` is for the genuinely stage-wide actions (approve-all,
  // redo-all, and — since it has no single focused card either — every
  // pick-one/moodboard command).
  const [inFlightIds, setInFlightIds] = useState<ReadonlySet<string>>(() => new Set());
  const [stageInFlight, setStageInFlight] = useState(false);
  const markInFlight = useCallback((id: string) => {
    setInFlightIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);
  const clearInFlight = useCallback((id: string) => {
    setInFlightIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);
  // crucible#2783 — assets the user has APPROVED whose approval hasn't landed
  // as `approved` in the polled snapshot yet. Under concurrent build-time
  // generation the engine acknowledges the approve POST quickly but applies the
  // manifest flip BEHIND the other in-flight generation work (observed 2+ min),
  // so `art:approve`/`art:command` resolve with a still-`staging` snapshot and
  // the invoke-lifetime `inFlightIds` busy state above clears immediately —
  // leaving a dead window where a genuinely-working approve reads as "nothing
  // happened / stuck". This set is DISTINCT from `inFlightIds`: it persists from
  // the approve verdict until a snapshot actually confirms the terminal status
  // (reconciled in `apply` below), so a slow approve reads as "saving" and stays
  // visually distinguishable from both a regenerate ("Tweaking") and a dropped
  // command (a real error banner). Membership is driven ONLY by unambiguous
  // approves — the direct `art:approve` (Keep it) and the SERVER's authoritative
  // `art:command` intent verdict (`approve`/`approve-stage`) — never by
  // classifying free-text client-side (see regex-not-for-semantic-classification).
  const [pendingApproveIds, setPendingApproveIds] = useState<ReadonlySet<string>>(() => new Set());
  const markPendingApprove = useCallback((ids: readonly string[]) => {
    if (ids.length === 0) return;
    setPendingApproveIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of ids) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);
  const clearPendingApprove = useCallback((ids: readonly string[]) => {
    setPendingApproveIds((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set(prev);
      for (const id of ids) {
        if (next.delete(id)) changed = true;
      }
      return changed ? next : prev;
    });
  }, []);
  const [redirectText, setRedirectText] = useState('');
  // While the FIRST snapshot is still failing, we keep a retrying spinner up
  // (the poll re-fires every 1.5s) instead of a bare red error. Only after
  // MAX_FIRST_LOAD_RETRIES consecutive failures do we surface a hard error.
  const [firstLoadRetrying, setFirstLoadRetrying] = useState(false);
  // crucible#2759 — main confirmed the review's engine is permanently gone
  // (every bounded relaunch attempt failed). Distinct from a transient poll
  // failure: those are silently ignored once a snapshot is showing (see the
  // poll's catch block below), which is exactly why a permanent death needs
  // its OWN signal — otherwise the panel just freezes on stale data forever.
  const [terminated, setTerminated] = useState(false);

  const doneFiredRef = useRef(false);
  const snapshotRef = useRef<SteeringSnapshot | null>(null);
  const firstLoadAttemptsRef = useRef(0);
  // crucible#2814 (mirrors crucible#2813's AssetReviewPanel fix) — set the
  // instant `asset-review:closed` decides the terminal state (finished OR
  // aborted). The 1.5s poll below keeps running independently of that push
  // (it's a separate effect) and, without this check, an in-flight or
  // subsequently-scheduled poll tick can still call `apply`/`setError` on a
  // stale pre-teardown snapshot — silently reverting `done: true` back to
  // `false`, or overwriting the 'aborted' error the push just surfaced. Once
  // terminal, the poll is permanently a no-op: the push is authoritative,
  // not just "first past the post". Distinct from `terminated` below (that
  // one is crucible#2759's "engine confirmed permanently unreachable" state,
  // which also disables the poll effect entirely via its dependency array).
  const terminalRef = useRef(false);
  // Read onDone through a ref so an inline parent callback doesn't recreate
  // `apply` (and thereby tear down + restart the 1.5s poll) on every render.
  const onDoneRef = useRef(onDone);
  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  const apply = useCallback((next: SteeringSnapshot) => {
    snapshotRef.current = next;
    firstLoadAttemptsRef.current = 0;
    setFirstLoadRetrying(false);
    setSnapshot(next);
    setRecoveryStatus(null);
    setError(null);
    // crucible#2783 — reconcile the pending-approve set against the freshly
    // applied snapshot. Runs on EVERY snapshot (poll tick OR command response),
    // so a slow approve's indicator self-heals the moment the engine's manifest
    // catches up. Keep an id pending ONLY while its asset is present and still
    // `staging`; clear it once the asset reaches `approved` (the happy path) or
    // any other resolution (rejected/blocked, or it dropped out of the snapshot
    // entirely). Functional updater so `apply` needs no extra dependency.
    setPendingApproveIds((prev) => {
      if (prev.size === 0) return prev;
      const byId = new Map(next.assets.map((a) => [a.id, a] as const));
      let changed = false;
      const nextSet = new Set(prev);
      for (const id of prev) {
        const asset = byId.get(id);
        if (!asset || asset.status !== 'staging') {
          nextSet.delete(id);
          changed = true;
        }
      }
      return changed ? nextSet : prev;
    });
    if (next.done && !doneFiredRef.current) {
      doneFiredRef.current = true;
      onDoneRef.current?.(next);
    }
  }, []);

  // crucible#3361 made per-card commands concurrent; crucible#3366 keeps their
  // out-of-order resolves from clobbering each other. `applySeqRef` is a
  // monotonic issue-order counter — each poll tick / command stamps its OWN seq
  // the moment it fires (BEFORE the async invoke), so "issued later" ≈ "built
  // from a more-recent server state". `stageSeqRef` remembers the seq whose
  // stage-level fields are currently shown, so a later-RESOLVING but
  // earlier-ISSUED (staler) response can't roll the stage back. See
  // `reconcileSteeringSnapshot` for the two rules (per-asset merge + stage seq).
  const applySeqRef = useRef(0);
  const stageSeqRef = useRef(0);
  const nextApplySeq = useCallback(() => (applySeqRef.current += 1), []);
  // Reconcile a resolved snapshot against what's on screen (per-asset merge +
  // stage-field seq) and commit it. `owned` is the id set this response is
  // authoritative for (`null` = the whole snapshot: a poll or a stage-wide
  // command). Returns the merged snapshot so a caller can read the authoritative
  // combined state (e.g. onKeepIt's focus auto-advance).
  const commitResolved = useCallback(
    (incoming: SteeringSnapshot, owned: OwnedAssetIds, issueSeq: number): SteeringSnapshot => {
      const { snapshot: merged, stageSeq } = reconcileSteeringSnapshot(
        { snapshot: snapshotRef.current, stageSeq: stageSeqRef.current },
        incoming,
        owned,
        issueSeq,
      );
      stageSeqRef.current = stageSeq;
      apply(merged);
      return merged;
    },
    [apply],
  );

  // crucible#2759 — subscribe to the terminal "relaunch exhausted" signal.
  // Reset on gameId change so a NEW review for a different game always
  // starts un-terminated, regardless of how the previous one ended. Guards
  // The scoped client supplies the same typed subscription contract in the
  // desktop host and in component tests.
  useEffect(() => {
    setTerminated(false);
    terminalRef.current = false;
    // crucible#2783 — a fresh review (mount / gameId change) starts with no
    // pending-approve indicators from a prior review.
    setPendingApproveIds(new Set());
    const unsubUnreachable = client.subscribe('asset-review:unreachable', (event) => {
      if (event.gameId !== gameId) return;
      setTerminated(true);
      // Bugbot MEDIUM (PR #3413 review, round 2) — same terminalRef guard as
      // `asset-review:closed` below: an unreachable review also permanently
      // stops the poll, so an in-flight approve invoke that resolves AFTER
      // this fires must not be allowed to re-mark pending (nothing would ever
      // clear it).
      terminalRef.current = true;
      // crucible#2783 — the review is gone; drop any "saving" indicator so it
      // can't outlive the surface it belongs to.
      setPendingApproveIds(new Set());
      setError(
        "Lost the connection while reviewing your art and couldn't reconnect. Restart the build to keep going.",
      );
    });
    // crucible#2814 (mirrors crucible#2813) — the poll above can only ever
    // observe a LAGGING view of "done": the review server can be torn down
    // (a genuine finish, or the surrounding build aborting) between two
    // polls, and once it's gone every subsequent poll throws — a failure the
    // catch block below deliberately swallows once a snapshot has already
    // landed (so a single transient blip doesn't flash an error over a
    // healthy review). That silence meant a finish that raced ahead of this
    // surface's poll left it stuck forever, showing its last pre-done state
    // with no way to ever learn otherwise and no `onDone` ever firing.
    // `asset-review:closed` removes the race: main pushes the terminal
    // reason explicitly, at the exact moment it tears the server down, so
    // this surface reacts to a fact instead of inferring one from an
    // ambiguous poll failure.
    const unsubClosed = client.subscribe('asset-review:closed', (event) => {
      if (event.gameId !== gameId) return;
      // Set BEFORE applying the terminal state, not after — a poll tick
      // already in flight (its invoke call sent, awaiting a response) checks
      // this flag when it resolves, so it must see `true` in time to skip
      // its own apply()/setError() no matter which of the two settles last.
      terminalRef.current = true;
      // crucible#2783 (Bugbot MEDIUM, PR #3413 review) — drop any pending-approve
      // indicator on EITHER terminal reason, up front. The poll stops the moment
      // this handler runs, so a slow approve still showing `staging` in
      // snapshotRef would otherwise keep its card stuck on "Saving" forever:
      // the `finished` path's `apply({...snapshotRef.current, done:true})` below
      // reconciles against that same still-`staging` asset and, without this,
      // would preserve the pending id with no future poll left to clear it.
      setPendingApproveIds(new Set());
      if (event.reason === 'finished') {
        // A snapshot may not exist yet if the review closed before this
        // surface's very first poll ever landed (extremely unlikely — a
        // review only exists once assets are already generating). Carry the
        // last known asset states forward, just flipping `done`, so the
        // done-pill/rendering stays consistent with how a poll-observed
        // `done: true` would have looked.
        if (snapshotRef.current) {
          apply({ ...snapshotRef.current, done: true });
          return;
        }
        // Bugbot MEDIUM (PR #2815 review) — with no snapshot, returning here
        // would leave `onDone` never fired: the poll is now blocked forever
        // by `terminalRef`, so the surface would stay mounted showing its
        // loading spinner with nothing left to tell the parent the review is
        // over. Synthesize a vacuous "nothing to review" completed snapshot
        // instead, so `apply` still fires `onDone` exactly the way a normal
        // `done: true` poll result would have. Safe for every known `onDone`
        // consumer (`Workspace.tsx`'s `summarizeNorthStarReview` only reads
        // `assets`/`approved`/`reviewable`/`stages`/`complete`/`finished`,
        // all of which are meaningfully zero/empty/true here).
        apply({
          reviewable: 0,
          approved: 0,
          staging: 0,
          rejected: 0,
          complete: true,
          finished: true,
          done: true,
          currentStageIndex: 0,
          stages: [],
          assets: [],
        });
        return;
      }
      // 'aborted' — the build failed/aborted for a reason unrelated to the
      // review itself. NEVER treat this as done (that would report a
      // failure as a fake success); surface it as a real, visible error and
      // stop polling (reuse the same stop-polling latch crucible#2759's
      // `unreachable` path already uses).
      setTerminated(true);
      setError(
        'The build was interrupted before this review finished. Restart the build to try again.',
      );
    });
    // Bugbot MEDIUM (PR #2764 review): the terminal latch above only reset on
    // a gameId CHANGE — restarting the build for the SAME game (exactly what
    // the terminal message tells the creator to do) re-launches a review that
    // pushes a fresh `asset-review:ready`, but this surface stayed mounted on
    // the old gameId and never heard it, so it stuck on the terminal banner
    // forever with polling permanently off. Clear the latch on a fresh ready
    // for THIS gameId so a restarted review recovers the surface too. Also
    // clears the crucible#2814 `terminalRef` + done latch for the same reason
    // (a retry after 'aborted' or a rebuilt 'finished' review must be able to
    // reach `onDone` again).
    const unsubReady = client.subscribe('asset-review:ready', (event) => {
      if (event.gameId !== gameId) return;
      setTerminated(false);
      terminalRef.current = false;
      doneFiredRef.current = false;
      setRecoveryStatus(null);
      // crucible#2783 (Bugbot MEDIUM, PR #3413 review) — a same-game restart
      // fires ready without a gameId change, so the effect body's reset above
      // doesn't run; clear pending-approve here too, or the restarted review
      // inherits stale "Saving" indicators from the prior run.
      setPendingApproveIds(new Set());
      setError(null);
    });
    return () => {
      unsubUnreachable();
      unsubClosed();
      unsubReady();
    };
  }, [apply, client, gameId]);

  // Poll the snapshot — keeps per-asset status + the moodboard candidate set live
  // as the engine generates takes or the creator works the stages. Stops for
  // good once `terminated` (crucible#2759) — a review confirmed permanently
  // gone is not coming back, so there is nothing left to poll for.
  useEffect(() => {
    if (terminated) return undefined;
    let cancelled = false;
    const tick = async (): Promise<void> => {
      // crucible#2814 (mirrors crucible#2813) — once `asset-review:closed`
      // has decided the terminal state, the poll must never touch it again
      // (see terminalRef's own doc above).
      if (terminalRef.current) return;
      // crucible#3366 — stamp the issue-order seq at tick START (before the
      // invoke), so a poll built from a more-recent server state can't be rolled
      // back by an earlier-issued command/poll that happens to resolve later.
      const issueSeq = nextApplySeq();
      try {
        // crucible#2611 Bugbot HIGH (round 4): this surface already knows its
        // OWN gameId (the `command` payload below always sends it) — the poll
        // must too. Without it, the poll falls back to the proxy's
        // most-recent-review projection, so a CONCURRENT Art-tab session for a
        // different game could silently steal this surface's snapshot: the
        // display shows game A's manifest while voice/text commands (which DO
        // carry gameId) mutate game B.
        const snap = await client.invoke('art:snapshot', { gameId });
        // A poll is authoritative for the whole snapshot (owned = null) — it is
        // the reconciler of record; the seq guard only protects stage-level
        // fields from a stale out-of-order resolve.
        if (!cancelled && !terminalRef.current) commitResolved(snap, null, issueSeq);
      } catch {
        if (cancelled || terminalRef.current) return;
        if (placement === 'local') {
          try {
            const status = await client.invoke('build-asset-review:status', { gameId });
            if (cancelled || terminalRef.current) return;
            setRecoveryStatus(status);
            if (status.state !== 'none' && status.state !== 'complete') {
              if (snapshotRef.current === null) {
                setFirstLoadRetrying(
                  status.state === 'starting' ||
                    status.state === 'generating' ||
                    status.state === 'waiting-for-review',
                );
              }
              setError(null);
              return;
            }
          } catch {
            /* bounded friendly fallback below */
          }
        }
        // A dropped poll after a snapshot is on screen can be transient, but
        // only durable status can prove that. The status probe above lets a
        // stalled/failed/stale worker replace the old snapshot with recovery
        // controls instead of freezing on stale art indefinitely.
        if (snapshotRef.current !== null) return;
        firstLoadAttemptsRef.current += 1;
        if (firstLoadAttemptsRef.current >= MAX_FIRST_LOAD_RETRIES) {
          setFirstLoadRetrying(false);
          setError('Your art preview is not available yet. Open Art or try the build again.');
        } else {
          setFirstLoadRetrying(true);
        }
      }
    };
    void tick();
    const handle = setInterval(() => void tick(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [client, gameId, terminated, commitResolved, nextApplySeq]);

  const view = snapshot ? currentStageView(snapshot) : null;
  const stage = (view?.stage ?? null) as MoodboardPickStage | null;
  const pickOne = isPickOneStage(stage);
  const consumerFrameActive = showPhoneDriverPreview || renderConsumerFrame !== undefined;
  const compactTvFrame = true;
  const pickCandidates = pickOne && stage?.candidates ? stage.candidates : [];
  const retryBuildReview = useCallback(() => {
    if (!recoveryStatus?.canRetry || !recoveryStatus.attemptId || retryingRecovery) return;
    const expectedReviewAttemptId = recoveryStatus.attemptId;
    setRetryingRecovery(true);
    void client
      .invoke('build-asset-review:retry', { gameId, expectedReviewAttemptId })
      .then((result) => {
        if (!result.ok) {
          setError(
            result.reason === 'already-running'
              ? 'Art generation is already restarting.'
              : 'This art attempt can no longer be retried. Open Art to continue.',
          );
          return;
        }
        terminalRef.current = false;
        setTerminated(false);
        setError(null);
        setRecoveryStatus({
          state: 'starting',
          attemptId: expectedReviewAttemptId,
          canRetry: false,
          reason: 'starting',
        });
      })
      .catch(() => setError('Foundry could not restart art generation. Open Art to continue.'))
      .finally(() => setRetryingRecovery(false));
  }, [client, gameId, recoveryStatus, retryingRecovery]);
  // crucible#3187 — the carousel's ordered card list for the current
  // (approve-each) stage. Empty during a pick-one stage; `pickCandidates`
  // above covers that degrade path instead.
  const cards = pickOne ? [] : carouselCards(view);

  // crucible#3187 — the phone carousel's "currently focused asset" concept.
  // Lives here (the parent), not inside the phone controller, so the TV can
  // mirror it (a later step adds the accent ring).
  //
  // Reconciled SYNCHRONOUSLY during render (React's supported "adjust state
  // while rendering" pattern — https://react.dev/reference/react/useState#storing-information-from-previous-renders),
  // deliberately NOT in a `useEffect`. An effect runs asynchronously relative
  // to the DOM commit, and testing-library's `findBy*`/`waitFor` resolve off
  // a DOM MutationObserver — they can observe the commit from a fresh
  // `art:snapshot` poll (the composer/pad are already in the DOM) BEFORE the
  // effect's own follow-up re-render (which would set the resolved focus)
  // ever fires, leaving the phone briefly stuck with no focused card (mic/
  // composer read as disabled, the pad reads "Loading…") — an actual bug this
  // surfaced as a flaky-looking test failure. Calling `reconcileFocus`
  // unconditionally here is safe: it's a deterministic, cheap scan, so once
  // `cards`' membership/statuses are stable it returns the SAME value every
  // subsequent render (React bails out — no extra commit); it only causes a
  // re-render when the resolved focus genuinely needs to change (a poll
  // advanced the stage or replaced the focused asset).
  const [focusedAssetId, setFocusedAssetId] = useState<string | null>(null);
  const resolvedFocusedAssetId = reconcileFocus(cards, focusedAssetId);
  if (resolvedFocusedAssetId !== focusedAssetId) {
    setFocusedAssetId(resolvedFocusedAssetId);
  }
  // crucible#3361 Bugbot MEDIUM (PR #3365 review) — read through a ref inside
  // onKeepIt's async continuation so the auto-advance below only fires if the
  // user hasn't ALREADY moved focus (prev/next/swipe stay enabled while an
  // approve is in flight, on purpose — you can keep browsing while a piece
  // resolves). Without this, a manual navigation that lands mid-flight gets
  // silently overwritten the moment the approve resolves.
  const resolvedFocusedAssetIdRef = useRef(resolvedFocusedAssetId);
  resolvedFocusedAssetIdRef.current = resolvedFocusedAssetId;
  const onStepFocus = useCallback(
    (direction: StepDirection) => {
      setFocusedAssetId(stepFocus(cards, resolvedFocusedAssetId, direction));
    },
    [cards, resolvedFocusedAssetId],
  );
  // #2671 follow-up (crucible#2700) — the "Approve all" button is a UI
  // affordance over the SAME command this function already sends for typed/
  // spoken text; it is NOT a second implementation. `overrideCommand` lets a
  // caller submit a canonical phrase without going through the textarea, so
  // voice, typed text, and the button all converge on this one function → the
  // same `art:command` channel (crucible#3112 — rewired off the legacy
  // `asset-review:command` invoke; the `asset-review:*` PUSH channels this
  // surface subscribes to below are unchanged) → the same backend
  // approve-stage loop (crucible#2699). Only clear the textarea when the
  // submitted command CAME from it — a button click must not wipe out
  // unrelated text mid-typed.
  //
  // crucible#3187 — now threads `targetId: resolvedFocusedAssetId` on every
  // call (harmless for "approve all"/"redo all": a stage-wide verdict still
  // wins over targetId per interpretAssetReviewCommand's priority order), and
  // scopes the in-flight busy affordance to just the focused card for an
  // organic composer submission — 'approve all' is still genuinely stage-wide
  // so it scopes busy to the whole stage.
  const submitPhoneCommand = useCallback(
    async (overrideCommand?: string) => {
      const fromTextarea = overrideCommand === undefined;
      const command = (overrideCommand ?? redirectText).trim();
      if (!command) {
        setError('Say or type what Foundry should do.');
        return;
      }
      // Bugbot MEDIUM (PR #3365 review, "Composer stage-wide skips stage
      // busy") — matching the LITERAL canonical phrase the button itself
      // sends, not classifying free text: a user who TYPES "approve all"
      // (rather than tapping the button) hit `overrideCommand === undefined`
      // here, so `isApproveAll` was false and busy scoped to just the
      // focused card even though the server treats it identically to the
      // button. This closes that one deterministic case. It does NOT close
      // every stage-wide phrase the LLM verdict can produce ("redo all
      // darker" etc.) — guessing those client-side would be exactly the
      // semantic-classification-by-regex this codebase's own
      // regex-not-for-semantic-classification rule forbids; that residual
      // gap needs the interpreter's OWN verdict, which isn't known until the
      // response lands, to close fully.
      const isApproveAll = command.trim().toLowerCase() === 'approve all';
      // Captured once up front so the wire payload and the busy-scoping
      // decision (start AND finally) all agree, even if `resolvedFocusedAssetId`
      // itself moves mid-flight (a concurrent Keep it elsewhere could change it).
      const focusedId = resolvedFocusedAssetId;
      // 'approve all' is genuinely stage-wide (it clears every open card in
      // the stage) even though a focused card may also be set — every OTHER
      // command scopes busy to just the focused card (crucible#3187's
      // targetId contract; `focusedId === null` falls back to stage-wide since
      // there's nothing narrower to scope to, e.g. a pick-one/moodboard command).
      const busyTargetId = isApproveAll ? null : focusedId;
      // crucible#3361 — defensive race backstop, mirroring the UI-disabled
      // state (this should never actually trip from a normal click; it guards
      // a click that slips in between a render and the disabled state
      // landing):
      //   - a per-card action (busyTargetId set) no-ops if THAT card already
      //     has a command in flight (the engine's single-take-replace history
      //     means two concurrent regenerations of the SAME asset would race
      //     each other — see the issue's backend-safety investigation).
      //   - a STAGE-WIDE action (busyTargetId null — approve-all, redo-all,
      //     or a pick-one command) no-ops if ANY card is currently regenerating,
      //     not just if another stage-wide op is running: approve-all touches
      //     every open asset, including one a per-card command is mid-flight
      //     on, so it must wait for that per-card work to clear too (Bugbot
      //     HIGH, PR #3365 review — the phone's focused-scoped `busy` alone
      //     let Approve-all run against a sibling still in flight once focus
      //     had moved off it).
      //   - the MIRROR gap (Bugbot MEDIUM, PR #3365 review): a per-card action
      //     also no-ops while a STAGE-WIDE op is in flight, not just when its
      //     OWN card is — a per-card command slipping in alongside a running
      //     approve-all/redo-all could otherwise race the same asset the
      //     stage-wide op is touching.
      // Bugbot MEDIUM (PR #3365 review, "Typed approve-all silently no-ops")
      // — the composer's `busy` prop is deliberately scoped to the focused
      // card (so sibling feedback stays usable), so it can't know ahead of
      // typing that THIS particular submit will turn out to be stage-wide;
      // send stays enabled and, without an explicit message here, the guard
      // below used to return with no feedback at all — indistinguishable
      // from the app just not responding. Surface it like the empty-command
      // case above, instead of a silent no-op.
      if (busyTargetId === null) {
        // Bugbot MEDIUM (PR #3413 review, round 4) — the Approve-all BUTTON's
        // disabled state now also checks pendingApproveIds (see anyInFlight),
        // but this guard is what actually blocks a stage-wide command from
        // firing — including a typed/spoken "approve all" that bypasses the
        // button entirely. Without the same check here, a card whose approve
        // has ACKed but isn't yet manifest-confirmed could still get run over
        // by a typed stage-wide command.
        if (stageInFlight || inFlightIds.size > 0 || pendingApproveIds.size > 0) {
          setError('Wait for the current piece to finish before approving everything.');
          return;
        }
      } else if (
        stageInFlight ||
        inFlightIds.has(busyTargetId) ||
        // Sibling of the round-4 fix above: this SAME card's own approve can
        // have ACKed (cleared from inFlightIds) but still be unconfirmed in
        // pendingApproveIds — a new command targeting it (e.g. a regenerate)
        // shouldn't race that unresolved approve either.
        pendingApproveIds.has(busyTargetId)
      ) {
        setError('Wait for the current work to finish before sending another change.');
        return;
      }
      if (busyTargetId === null) {
        setStageInFlight(true);
      } else {
        markInFlight(busyTargetId);
      }
      // crucible#3361 — clear the composer OPTIMISTICALLY at submit time, not
      // on success: with concurrent per-card feedback, clearing on success
      // would wipe out whatever the user has since typed for a DIFFERENT card
      // while THIS one is still regenerating.
      if (fromTextarea) setRedirectText('');
      // crucible#3366 — stamp the issue-order seq before the invoke (see
      // applySeqRef), so a slower-resolving stale sibling command can't roll
      // this one's stage-level fields back.
      const issueSeq = nextApplySeq();
      try {
        // crucible#3187 — every typed/spoken command goes through art:command;
        // the SERVER (commandAssetReview + interpretAssetReviewCommand) owns ALL
        // routing off a FRESH, authoritative snapshot: the named-asset-wins
        // priority order, the focused-card default (targetId), AND the
        // "regenerate an already-approved target → unapprove-then-redo" upgrade
        // that used to live here as a renderer-side art:redo shortcut. That
        // shortcut keyed on the renderer's `focusedCard.status`, which lags the
        // engine by a poll: mid-stage, focus can still read a just-approved card
        // while the creator names the NEXT (freshly-generated) one, so the
        // shortcut redid the wrong card and the named one was never touched
        // (the gui-northstar-nospend staging-stuck failure). Deciding on the
        // server's fresh snapshot instead of stale local state removes the race.
        const result = await client.invoke('art:command', {
          gameId,
          command,
          source: 'text',
          targetId: pickOne ? undefined : (focusedId ?? undefined),
        });
        // crucible#3366 — merge by the ids this response actually owns (a
        // per-card verdict owns just its target; a rejected command owns
        // nothing; a stage-wide verdict owns the whole snapshot), so a
        // concurrent sibling command's fresher status is preserved.
        const owned = ownedAssetIdsFromCommandResult(result);
        if (!result.ok) {
          setError(result.intent.reason);
          if (result.snapshot) commitResolved(result.snapshot as SteeringSnapshot, owned, issueSeq);
          return;
        }
        setError(null);
        // crucible#2783 — the SERVER's authoritative intent verdict tells us
        // which assets this command actually approved (never a client-side
        // guess off the raw text). Mark them pending BEFORE committing the
        // returned snapshot so the reconcile immediately clears any that
        // already landed `approved`, while any still-`staging` under concurrent
        // generation load stay pending until a later poll confirms them.
        //
        // Bugbot MEDIUM (PR #3413 review, round 2) — this invoke can resolve
        // AFTER `asset-review:closed`/`unreachable` already fired, cleared
        // pendingApproveIds, and stopped polling. Re-marking pending here with
        // nothing left to ever clear it would leave a stuck "Saving" indicator
        // — skip it once the review has ended.
        if (!terminalRef.current) {
          if (result.intent.kind === 'approve') {
            markPendingApprove([result.intent.targetId]);
          } else if (result.intent.kind === 'approve-stage') {
            markPendingApprove(result.intent.targetIds);
          }
        }
        // crucible#3366 — merge by the ids this response actually owns instead
        // of replacing the whole snapshot, so a concurrent sibling command's
        // fresher status survives.
        commitResolved(result.snapshot as SteeringSnapshot, owned, issueSeq);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (busyTargetId === null) {
          setStageInFlight(false);
        } else {
          clearInFlight(busyTargetId);
        }
      }
    },
    [
      commitResolved,
      nextApplySeq,
      client,
      gameId,
      redirectText,
      pickOne,
      resolvedFocusedAssetId,
      inFlightIds,
      stageInFlight,
      markInFlight,
      clearInFlight,
      markPendingApprove,
      // Bugbot MEDIUM (PR #3413 review, round 5) — the round-4 fix reads
      // pendingApproveIds inside this callback's busy guards but omitted it
      // here, so the callback stayed a stale closure over whatever value was
      // captured at its last real recreation: once a poll cleared pending in
      // React state, this callback could keep seeing the old non-empty set
      // (and reject Approve-all / typed sends with the wait message) until
      // some OTHER dependency happened to force a fresh closure.
      pendingApproveIds,
    ],
  );
  const submitApproveAll = useCallback(() => {
    void submitPhoneCommand('approve all');
  }, [submitPhoneCommand]);
  // crucible#3187 — "Keep it" is a DIRECT art:approve call keyed on the
  // focused card, bypassing art:command / the LLM verdict classification
  // entirely (per the issue's backend mapping: "Keep it -> art:approve
  // {id: focusedAssetId, gameId} -> approveAsset. NO new surface."). The
  // button is disabled whenever there's no focused card to approve
  // (PhoneSteeringController's own gating), but this still guards defensively.
  //
  // crucible#3361 — on a successful approve, ALSO advance focus to the next
  // open card (`advanceFocusAfterApproval`), so tapping Keep it repeatedly
  // walks the whole stage without any manual nav. Deliberately scoped to
  // THIS button only — not `submitApproveAll` (clears the whole stage, so
  // there's nothing to advance to) and not a composer regenerate (the user is
  // iterating that piece; keep focus on it). Recomputed off the MERGED snapshot
  // (crucible#3366 — the authoritative combined state, not the render-closure
  // `cards` nor this one response's raw view), so a concurrent regeneration
  // elsewhere doesn't advance onto a stale status.
  //
  // Bugbot MEDIUM (PR #3365 review) — only auto-advance if focus is STILL on
  // the just-approved card. Nav stays enabled while the approve is in flight
  // (by design — you can keep browsing), so the user may have already swiped
  // elsewhere by the time this resolves; auto-advancing then would yank focus
  // back off the card they deliberately chose.
  const onKeepIt = useCallback(() => {
    if (!resolvedFocusedAssetId) return;
    const targetId = resolvedFocusedAssetId;
    // crucible#3361 — same-asset-race backstop, AND (Bugbot MEDIUM, PR #3365
    // review) don't let Keep it slip in alongside a running stage-wide op
    // (approve-all/redo-all) either — see submitPhoneCommand's matching guard
    // above for the full rationale.
    if (stageInFlight || inFlightIds.has(targetId)) return;
    markInFlight(targetId);
    // crucible#2783 — Keep it is an unambiguous single-asset approve, so mark
    // it pending the instant it's sent (before the await). `art:approve`
    // resolves once the engine ACKs the POST, which under concurrent generation
    // load can be well before the manifest flips to `approved` — the pending
    // set (cleared only once the reconciled snapshot confirms it) keeps a
    // "saving" indicator up across that gap instead of the card snapping back
    // to an idle `staging` state.
    markPendingApprove([targetId]);
    // crucible#3366 — stamp the issue-order seq before the invoke (see
    // applySeqRef), so a slower stale sibling response can't roll this
    // approval's stage-level fields back.
    const issueSeq = nextApplySeq();
    void (async () => {
      try {
        const snap = (await client.invoke('art:approve', {
          id: targetId,
          gameId,
        })) as SteeringSnapshot;
        setError(null);
        // Keep it approves exactly this card → the response is authoritative
        // for that id only; merge by it so a concurrent command's fresher
        // sibling status survives (crucible#3366).
        const merged = commitResolved(snap, new Set([targetId]), issueSeq);
        if (resolvedFocusedAssetIdRef.current === targetId) {
          // Bugbot MEDIUM (PR #3365 review, "Keep-it leaves retargeted
          // composer text") — any draft text still in the composer was
          // typed for the card we're LEAVING via auto-advance, not the one
          // we're about to land on; a later send would otherwise apply it to
          // the wrong asset. Clear it ONLY here (not unconditionally on
          // every Keep-it success) — if the user already navigated away
          // mid-flight, this branch doesn't run at all, so a draft they
          // typed for the card they're now actually looking at survives
          // untouched.
          setRedirectText('');
          setFocusedAssetId(
            advanceFocusAfterApproval(carouselCards(currentStageView(merged)), targetId),
          );
        }
      } catch (err) {
        // crucible#2783 — a genuinely dropped/failed approve must read
        // distinctly from a slow one: clear the "saving" indicator and surface
        // the error banner, rather than leaving a pending spinner up forever.
        clearPendingApprove([targetId]);
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        clearInFlight(targetId);
      }
    })();
  }, [
    commitResolved,
    nextApplySeq,
    client,
    gameId,
    resolvedFocusedAssetId,
    inFlightIds,
    stageInFlight,
    markInFlight,
    clearInFlight,
    markPendingApprove,
    clearPendingApprove,
  ]);
  // #2671 — the button is ALWAYS rendered for an approve-each stage (never
  // hidden); only its enabled state changes. `hidden` is reserved for the
  // pick-one degrade path (batch-approving N candidates isn't coherent when
  // picking exactly one look).
  const approveAllState: ApproveAllState = pickOne
    ? 'hidden'
    : (stage?.approved ?? 0) >= 1
      ? 'enabled'
      : 'disabled-gate';
  // crucible#3361 — the composer/Keep it only gate on whether the piece
  // they're actually pointing at is busy (or a genuinely stage-wide op is
  // running) — NOT on whether any sibling card elsewhere is regenerating.
  // This is what lets the composer/Keep it stay usable on card B while card A
  // is still cooking.
  const focusedInFlight =
    resolvedFocusedAssetId !== null && inFlightIds.has(resolvedFocusedAssetId);
  // crucible#2783 — the focused card's approve is sent but not yet confirmed
  // `approved` in the snapshot. Drives the phone Keep it button's "Saving…"
  // state so returning focus to a still-saving card reads as in-progress
  // rather than re-offering an approve that's already on its way.
  const focusedPendingApprove =
    resolvedFocusedAssetId !== null && pendingApproveIds.has(resolvedFocusedAssetId);
  // Approve-all is different: it's stage-wide (touches EVERY open card), so
  // it must stay disabled while ANY card is regenerating, not just the
  // focused one — otherwise navigating off a mid-flight card re-enables
  // Approve-all and lets it run against an asset a per-card command is still
  // mid-flight on (Bugbot HIGH, PR #3365 review).
  //
  // Bugbot MEDIUM (PR #3413 review) — invoke-lifetime busy state clears the
  // instant an approve ACKs, well before the manifest confirms it (the whole
  // reason pendingApproveIds exists). Without also checking it here,
  // Approve-all re-enables and can fire a stage-wide approve against cards
  // still `pendingApproveIds`-pending, unconfirmed.
  const anyInFlight = stageInFlight || inFlightIds.size > 0 || pendingApproveIds.size > 0;
  const showRecoveryPanel =
    recoveryStatus !== null &&
    recoveryStatus.state !== 'none' &&
    recoveryStatus.state !== 'complete' &&
    (snapshot === null || blocksStaleAssetReviewSnapshot(recoveryStatus));
  // crucible#4051 defense-in-depth — a pick-one stage's phone panel is
  // suppressed when the flag is off (see the useFeatureFlags() docblock
  // above); an approve-each stage's phone panel is UNCHANGED either way, so
  // this only removes the pick-one-specific "Pick a look with Foundry"
  // degrade layout, never the ordinary carousel controller.
  const phoneDriver =
    pickOne && !buildTimeAssetReviewEnabled ? null : (
      <PhoneSteeringController
        cards={cards}
        focusedAssetId={resolvedFocusedAssetId}
        onStepFocus={onStepFocus}
        pickCandidates={pickCandidates}
        busy={stageInFlight || focusedInFlight}
        approveAllBusy={anyInFlight}
        focusedPendingApprove={focusedPendingApprove}
        feedback={redirectText}
        onFeedbackChange={setRedirectText}
        onSubmit={() => void submitPhoneCommand()}
        onKeepIt={onKeepIt}
        approveAllState={approveAllState}
        onApproveAll={submitApproveAll}
        testIds={{
          root: 'consumer-phone-steering-driver',
          voiceButton: 'consumer-phone-voice-button',
          voiceError: 'consumer-phone-voice-error',
          guidance: 'consumer-phone-guidance',
          approveAll: 'consumer-phone-approve-all',
          composer: 'consumer-phone-command-composer',
          composerInput: 'consumer-phone-command-input',
          composerSend: 'consumer-phone-command-send',
          pad: 'phone-steer-pad',
          dots: 'phone-steer-dots',
          focusedLabel: 'phone-steer-focused-label',
          prev: 'phone-steer-prev',
          next: 'phone-steer-next',
          keepIt: 'phone-keep-it',
        }}
      />
    );

  const tvSurface = (
    <div
      className="tv-steering-shell absolute inset-0 z-10 flex flex-col overflow-hidden bg-[#0b0911] text-white"
      data-testid="tv-steering-surface"
      data-game-id={gameId}
      data-current-stage={stage?.kind ?? ''}
      data-mode={pickOne ? 'pick-one' : 'approve-each'}
      data-couch-build-preview={consumerFrameActive ? 'true' : 'false'}
      role="region"
      aria-label="Review your game art"
    >
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div
        className={[
          'relative z-10 flex shrink-0 items-start justify-between',
          compactTvFrame
            ? 'gap-3 px-[clamp(18px,2.8vw,32px)] pb-1 pt-[clamp(12px,1.8vh,20px)]'
            : 'gap-8 px-[max(32px,4vw)] pb-5 pt-[max(28px,3vh)]',
        ].join(' ')}
      >
        <div className={compactTvFrame ? 'min-w-0 max-w-[38rem]' : 'min-w-0 max-w-[68rem]'}>
          <p
            className={[
              'font-semibold uppercase text-crucible-accent',
              compactTvFrame ? 'text-[clamp(11px,0.8vw,14px)]' : 'text-[clamp(13px,1vw,18px)]',
            ].join(' ')}
          >
            Build the look
          </p>
          {stage ? (
            <h2
              className={[
                'font-semibold leading-[1.02] text-white',
                compactTvFrame
                  ? 'mt-1 text-[clamp(22px,2vw,34px)]'
                  : 'mt-2 text-[clamp(34px,4vw,68px)]',
              ].join(' ')}
              data-testid="tv-steering-stage-title"
            >
              {pickOne ? <>Pick the look</> : <>Shape the {stage.label.toLowerCase()}</>}
            </h2>
          ) : (
            <p className="mt-2 text-[clamp(24px,2.5vw,44px)] leading-tight text-white/80">
              Approve each piece or ask for a change.
            </p>
          )}
          {!compactTvFrame && (
            <p className="mt-4 max-w-[52rem] text-[clamp(18px,1.35vw,26px)] leading-snug text-white/62">
              {pickOne
                ? 'Foundry will guide the look choice. Say which direction to keep or change.'
                : 'Foundry will guide this piece. Say what to keep or what should change.'}
            </p>
          )}
          {compactTvFrame && view && (
            <p
              className="mt-2 max-w-[42rem] text-[clamp(13px,1vw,17px)] leading-snug text-white/58"
              data-testid="tv-steering-command-prompt"
            >
              {pickOne
                ? 'Foundry is guiding the look. Say what to keep or change.'
                : 'Foundry is guiding this piece. Say what to keep or change.'}
            </p>
          )}
        </div>
        {snapshot && view && !showRecoveryPanel && (
          <span
            className={[
              'shrink-0 rounded-full border border-white/10 bg-white/10 font-semibold text-white/82',
              compactTvFrame
                ? 'px-3 py-1.5 text-[clamp(12px,0.95vw,17px)]'
                : 'px-5 py-2.5 text-[clamp(17px,1.2vw,24px)]',
            ].join(' ')}
            data-testid="tv-steering-done-pill"
          >
            {stage
              ? stageProgressLabel(stage, view.stageNumber, view.stageCount, snapshot.done)
              : `${snapshot.approved}/${snapshot.reviewable} approved`}
            {snapshot.done ? ' · Done' : ''}
          </span>
        )}
      </div>

      {/* ── Stage rail ──────────────────────────────────────────────────────── */}
      {view && !showRecoveryPanel && view.stageCount > 0 && (
        <div
          className={[
            'relative z-10 flex shrink-0',
            compactTvFrame
              ? 'flex-wrap gap-1.5 overflow-visible px-[clamp(18px,2.8vw,32px)] pb-1'
              : 'flex-wrap gap-2.5 px-[max(32px,4vw)] pb-4',
          ].join(' ')}
          data-testid="tv-steering-stage-rail"
        >
          {(snapshot!.stages as MoodboardPickStage[])
            // crucible#4051 defense-in-depth — a pick-one stage's own rail
            // chip is excluded when the flag is off (see the
            // useFeatureFlags() docblock above), so the rail carries no
            // trace of the suppressed stage even if one somehow exists.
            // `s.index` (what `current` compares against) is untouched by
            // filtering, so the highlight on the remaining chips stays correct.
            .filter((s) => buildTimeAssetReviewEnabled || s.mode !== 'pick-one')
            .map((s) => (
              <StageChip
                key={s.kind}
                stage={s}
                current={s.index === view.stageNumber - 1}
                compact={compactTvFrame}
              />
            ))}
        </div>
      )}

      {/* ── Body ────────────────────────────────────────────────────────────── */}
      <div
        className={[
          'relative z-10 flex min-h-0 flex-1 items-center justify-center overflow-hidden',
          compactTvFrame ? 'px-[clamp(18px,2.8vw,32px)] py-1' : 'px-[max(32px,4vw)] py-4',
        ].join(' ')}
      >
        {/* Error */}
        {error && (
          <div
            className="absolute left-[max(32px,4vw)] right-[max(32px,4vw)] top-4 rounded-2xl bg-red-500/15 p-4 text-[clamp(16px,1.2vw,22px)] text-red-200"
            role="alert"
          >
            {error}
          </div>
        )}

        {showRecoveryPanel && recoveryStatus && (
          <div
            className="flex w-full max-w-3xl flex-col items-center gap-4 rounded-3xl border border-white/10 bg-black/25 p-8 text-center"
            data-testid="tv-steering-recovery"
            data-recovery-state={recoveryStatus.state}
            role={recoveryStatus.canRetry ? 'alert' : 'status'}
            aria-live="polite"
          >
            <h3 className="text-[clamp(24px,2vw,38px)] font-semibold text-white">
              {recoveryStatus.state === 'starting'
                ? 'Reconnecting to your art session…'
                : recoveryStatus.state === 'generating'
                  ? 'Creating your next art preview…'
                  : recoveryStatus.state === 'waiting-for-review'
                    ? 'Your art is ready'
                    : recoveryStatus.state === 'stalled'
                      ? 'Art generation stopped'
                      : recoveryStatus.state === 'stale'
                        ? 'This review belongs to an earlier build'
                        : 'Art generation needs another try'}
            </h3>
            <p className="text-[clamp(15px,1.1vw,20px)] leading-relaxed text-white/62">
              {recoveryStatus.reason === 'another-review-active'
                ? 'Another Art session is already open for this project.'
                : recoveryStatus.reason === 'cancelled'
                  ? 'This build was cancelled. Open Art to continue or start a new build.'
                  : recoveryStatus.reason === 'previous-session'
                    ? 'Foundry is checking the previous generation worker.'
                    : recoveryStatus.state === 'stale'
                      ? 'The project changed after this review started.'
                      : recoveryStatus.state === 'stalled'
                        ? 'Foundry lost contact with the generation worker.'
                        : 'Your saved work is safe while Foundry reconnects.'}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {recoveryStatus.canRetry && recoveryStatus.attemptId && (
                <button
                  type="button"
                  className="rounded-full bg-crucible-accent px-5 py-2.5 font-semibold text-black disabled:opacity-55"
                  disabled={retryingRecovery}
                  onClick={retryBuildReview}
                >
                  {retryingRecovery ? 'Restarting…' : 'Retry generation'}
                </button>
              )}
              {onOpenArt && (
                <button
                  type="button"
                  className="rounded-full border border-white/20 px-5 py-2.5 font-semibold text-white"
                  onClick={onOpenArt}
                >
                  Open Art
                </button>
              )}
            </div>
          </div>
        )}

        {/* Loading — including the first-load retry beat (keep a spinner up while
            the snapshot is still coming, never a bare error mid-retry). */}
        {!snapshot &&
          !error &&
          (!recoveryStatus ||
            recoveryStatus.state === 'none' ||
            recoveryStatus.state === 'complete') && (
            <div
              className="flex items-center gap-4 py-6 text-[clamp(22px,2vw,34px)] text-white/65"
              data-testid="tv-steering-loading"
              data-retrying={firstLoadRetrying ? 'true' : 'false'}
              role="status"
              aria-live="polite"
            >
              <span className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-crucible-accent" />
              {firstLoadRetrying
                ? 'Still loading your art… (retrying)'
                : 'Loading your generated art…'}
            </div>
          )}

        {/* MOODBOARD pick-one — 3 candidates side-by-side; pick ONE.
            crucible#4051 defense-in-depth — gated on buildTimeAssetReviewEnabled
            too, so this never renders even if pickOne/stage somehow evaluate
            truthy while the flag is off (see the useFeatureFlags() docblock
            above). */}
        {!showRecoveryPanel && view && pickOne && stage && buildTimeAssetReviewEnabled && (
          <MoodboardPickRow
            candidates={stage.candidates!}
            busy={stageInFlight}
            compact={compactTvFrame}
          />
        )}

        {/* EVERY OTHER stage — per-asset YES / NO grid. */}
        {!showRecoveryPanel && view && !pickOne && view.assets.length > 0 && (
          <div
            className={
              compactTvFrame
                ? view.assets.length === 1
                  ? 'mx-auto grid h-full w-full max-w-[min(68vw,900px)] grid-cols-1 place-items-center overflow-hidden'
                  : 'grid max-h-full w-full grid-cols-1 gap-3 overflow-hidden md:grid-cols-2 2xl:grid-cols-3'
                : view.assets.length === 1
                  ? 'mx-auto grid w-full max-w-[min(44vw,640px)] grid-cols-1 place-items-center'
                  : 'grid max-h-full w-full grid-cols-1 gap-5 overflow-hidden md:grid-cols-2 2xl:grid-cols-3'
            }
            data-testid="tv-steering-asset-grid"
          >
            {view.assets.map((asset) => (
              <SteeringAssetCard
                key={asset.id}
                asset={asset}
                busy={stageInFlight || inFlightIds.has(asset.id)}
                pendingApprove={pendingApproveIds.has(asset.id)}
                compact={compactTvFrame}
                focused={asset.id === resolvedFocusedAssetId}
              />
            ))}
          </div>
        )}

        {/* Current stage still GENERATING (no assets / candidates landed yet).
            An animated indeterminate progress bar (not a static placeholder) so
            the surface reads as actively working, not frozen/broken. */}
        {!showRecoveryPanel &&
          view &&
          !pickOne &&
          view.assets.length === 0 &&
          stage &&
          !view.allStagesApproved && (
            <div
              className="flex w-full max-w-3xl flex-col items-center gap-5 py-6 text-center"
              data-testid="tv-steering-generating"
              data-generating="true"
              role="status"
              aria-live="polite"
            >
              <div className="flex items-center gap-4 text-[clamp(24px,2vw,38px)] text-white/78">
                <span className="h-9 w-9 animate-spin rounded-full border-2 border-white/20 border-t-crucible-accent" />
                Creating your {stage.label.toLowerCase()}…
              </div>
              <div
                className="tv-steering-progress-track h-2.5 w-full rounded-full bg-white/10"
                data-testid="tv-steering-generating-progress"
                aria-hidden="true"
              />
            </div>
          )}
      </div>

      {showPhoneDriverPreview && !renderConsumerFrame && !showRecoveryPanel && (
        <div
          className="pointer-events-none absolute bottom-[max(24px,3vh)] right-[max(24px,3vw)] top-[max(112px,14vh)] z-20 hidden w-[min(26vw,360px)] min-w-[280px] max-w-[380px] xl:block"
          data-testid="consumer-phone-driver-preview"
          data-consumer-surface="phone-driver"
          data-input-primitive="voice-first-text-fallback"
          data-phone-driver-ready="true"
        >
          {phoneDriver}
        </div>
      )}
    </div>
  );

  if (renderConsumerFrame) {
    return renderConsumerFrame({ tv: tvSurface, phone: phoneDriver });
  }

  return tvSurface;
}

// ── MoodboardPickRow ────────────────────────────────────────────────────────────

/** The 3-up moodboard display. The TV shows choices; the phone command input acts. */
function MoodboardPickRow({
  candidates,
  busy: commandInFlight,
  compact = false,
}: {
  candidates: MoodboardCandidate[];
  /** A phone command is in flight — every live (non-discarded) candidate shows the busy state. */
  busy: boolean;
  compact?: boolean;
}): React.JSX.Element {
  return (
    <div
      className={[
        'grid w-full grid-cols-1',
        compact ? 'gap-3 sm:grid-cols-3' : 'gap-5 sm:grid-cols-2 lg:grid-cols-3',
      ].join(' ')}
      data-testid="tv-steering-moodboard-row"
    >
      {candidates.map((c) => {
        const url = candidateImageUrl(c);
        const dropped = c.status === 'discarded';
        const picked = c.status === 'picked';
        const busy = commandInFlight && !dropped;
        const label = candidateDisplayLabel(candidates.indexOf(c));
        // No image yet AND actively generating (not a dropped/discarded slot) →
        // show animated shimmer, not a static "no preview".
        const generating = !url && c.status === 'generating' && !dropped;
        return (
          <div
            key={c.id}
            data-testid={`tv-steering-moodboard-candidate-${c.id}`}
            data-candidate-state={picked ? 'picked' : dropped ? 'dropped' : 'staging'}
            aria-label={label}
            className={[
              'group relative flex flex-col overflow-hidden border bg-black/30 text-left shadow-2xl shadow-black/25 transition',
              compact ? 'rounded-[20px]' : 'rounded-[32px]',
              picked ? 'border-crucible-accent ring-2 ring-crucible-accent/60' : 'border-white/10',
              'cursor-default',
              dropped ? 'pointer-events-none opacity-30' : '',
            ].join(' ')}
          >
            <div className="relative aspect-video w-full bg-white/5">
              {url ? (
                <img src={url} alt={label} className="h-full w-full object-cover" />
              ) : generating ? (
                <div
                  className={[
                    'tv-steering-shimmer flex h-full w-full flex-col items-center justify-center',
                    compact ? 'gap-1' : 'gap-1.5',
                  ].join(' ')}
                  data-testid={`tv-steering-moodboard-generating-${c.id}`}
                  data-generating="true"
                  role="status"
                  aria-label={`Generating look ${c.id}`}
                >
                  <span
                    className={[
                      'animate-spin rounded-full border-2 border-white/15 border-t-crucible-accent',
                      compact ? 'h-5 w-5' : 'h-8 w-8',
                    ].join(' ')}
                  />
                  <span
                    className={[
                      'uppercase text-white/45',
                      compact ? 'text-[clamp(10px,0.7vw,12px)]' : 'text-[clamp(14px,1vw,18px)]',
                    ].join(' ')}
                  >
                    Generating…
                  </span>
                </div>
              ) : (
                <div
                  className={[
                    'flex h-full w-full items-center justify-center uppercase text-white/40',
                    compact ? 'text-[clamp(10px,0.7vw,12px)]' : 'text-[clamp(15px,1vw,20px)]',
                  ].join(' ')}
                  data-testid={`tv-steering-moodboard-no-preview-${c.id}`}
                >
                  no preview
                </div>
              )}
            </div>
            <div
              className={[
                'flex items-center justify-between gap-2',
                compact ? 'px-3 py-2' : 'px-5 py-4',
              ].join(' ')}
            >
              <span
                className={[
                  'font-semibold text-white/88',
                  compact ? 'text-[clamp(12px,0.85vw,15px)]' : 'text-[clamp(18px,1.35vw,25px)]',
                ].join(' ')}
              >
                {label}
              </span>
              <span
                className={[
                  'shrink-0 font-semibold uppercase tracking-[0.14em]',
                  picked ? 'text-crucible-accent' : busy ? 'text-white/72' : 'text-white/38',
                  compact ? 'text-[clamp(9px,0.62vw,11px)]' : 'text-[clamp(12px,0.9vw,16px)]',
                ].join(' ')}
                data-testid={busy ? `tv-steering-moodboard-reshaping-${c.id}` : undefined}
              >
                {busy ? 'Tweaking' : picked ? 'Picked' : 'Option'}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function stageProgressLabel(
  stage: MoodboardPickStage,
  stageNumber: number,
  stageCount: number,
  done: boolean,
): string {
  const prefix = `Step ${stageNumber}/${stageCount}`;
  if (done || stage.status === 'approved') return `${prefix} · approved`;
  if (stage.status === 'pending' || stage.status === 'generating') {
    return `${prefix} · generating`;
  }
  if (stage.mode === 'pick-one') return `${prefix} · choose 1`;
  if (stage.total <= 0) return `${prefix} · generating`;
  const remaining = Math.max(0, stage.total - stage.approved);
  return `${prefix} · ${remaining} to review`;
}

// ── StageChip ───────────────────────────────────────────────────────────────────

function StageChip({
  stage,
  current,
  compact = false,
}: {
  stage: MoodboardPickStage;
  current: boolean;
  compact?: boolean;
}): React.JSX.Element {
  const approved = stage.status === 'approved';
  const cls = current
    ? 'border-crucible-accent/70 bg-crucible-accent/15 text-white'
    : approved
      ? 'border-green-500/40 bg-green-500/10 text-green-300/90'
      : 'border-white/10 bg-white/5 text-white/40';
  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 rounded-full border font-semibold',
        compact
          ? 'max-w-[10.5rem] shrink-0 truncate px-2.5 py-1 text-[clamp(11px,0.78vw,14px)]'
          : 'px-4 py-2 text-[clamp(14px,0.95vw,18px)]',
        cls,
      ].join(' ')}
      data-testid={`tv-steering-stage-chip-${stage.kind}`}
      data-stage-status={stage.status}
      data-stage-current={current ? 'true' : 'false'}
    >
      {approved ? '✓ ' : ''}
      {stage.label}
    </span>
  );
}

// ── SteeringAssetCard ─────────────────────────────────────────────────────────────

/** One image-first asset tile. The TV shows the art plus a compact descriptor;
 *  keep/tweak/redo commands stay on the phone/right rail. */
function SteeringAssetCard({
  asset,
  busy,
  pendingApprove = false,
  compact = false,
  focused = false,
}: {
  asset: AssetReviewItem;
  busy: boolean;
  /** crucible#2783 — an approve for this asset is sent but not yet confirmed
   *  `approved` in the snapshot. Shows a distinct "Saving" indicator so a slow
   *  approve (concurrent build-time generation delaying the manifest flip)
   *  reads as working, not stuck. */
  pendingApprove?: boolean;
  compact?: boolean;
  /** crucible#3187 — the phone carousel's currently-focused card. The
   *  PRIMARY carousel motion lives here: the accent ring + slight scale
   *  moves between cards as the phone steps focus, animated by the bare
   *  `transition` utility already in this classlist (Tailwind's default
   *  transition covers border-color/box-shadow/transform — no bespoke
   *  keyframe needed, unlike the phone's own name-crossfade in globals.css). */
  focused?: boolean;
}): React.JSX.Element {
  const approved = asset.status === 'approved';
  // crucible#2783 — `approved` (confirmed) supersedes a pending indicator, so a
  // card never shows both "Kept" and "Saving".
  const pending = pendingApprove && !approved;
  const label = assetDisplayLabel(asset);
  const descriptor = assetTvDescriptor(asset);
  const statusLabel = assetTvStatusLabel(asset, busy, pending);
  return (
    <div
      className={[
        'flex max-h-full w-full flex-col border bg-black/18 shadow-2xl shadow-black/25 transition',
        compact
          ? 'max-h-full gap-2 overflow-hidden rounded-[18px] p-1.5'
          : 'gap-3 rounded-[28px] p-3',
        focused
          ? 'scale-[1.015] border-crucible-accent ring-2 ring-crucible-accent/60'
          : 'border-white/10',
      ].join(' ')}
      data-testid={`tv-steering-asset-card-${asset.id}`}
      data-asset-status={asset.status}
      data-pending-approve={pending ? 'true' : 'false'}
      data-focused={focused ? 'true' : 'false'}
    >
      {/* Thumbnail */}
      <div
        className={[
          'relative aspect-video w-full overflow-hidden bg-white/5',
          compact ? 'rounded-[16px]' : 'rounded-[28px]',
        ].join(' ')}
        // #2865 — the transparency checkerboard used to live as inline
        // `style` on the `<img>` below, which ALSO carries the
        // `tv-steering-reveal-in` mount animation. That animation's `filter:
        // blur(7px)→blur(0)` blurs the WHOLE element it's applied to,
        // including any CSS background painted on that same element — so for
        // the ~700ms reveal window right after the image mounts, the
        // checkerboard smeared into a soft, low-contrast vignette that blended
        // into the surrounding dark chrome (reading as "full-bleed, no
        // margin"). Only once the blur settled did the true letterboxed-
        // with-transparency look "pop" in. In a fast review flow (a driver
        // command sent right after Ready), that pop lands at almost the same
        // moment the busy/"Tweaking" state shows, making it look like
        // approving/steering the asset removed its background — it didn't;
        // the underlying image never changed. Moving the checkerboard onto
        // this STATIC wrapper (never animated) means it renders at full
        // clarity from the asset's very first paint; the `<img>` keeps the
        // reveal-in flourish for the photo pixels only.
        style={asset.imageUrl ? TRANSPARENCY_CHECKERBOARD_STYLE : undefined}
      >
        {asset.imageUrl ? (
          <img
            key={asset.imageUrl}
            src={asset.imageUrl}
            alt={label}
            className="tv-steering-reveal-in h-full w-full object-contain"
          />
        ) : asset.category === 'audio' ? (
          <div
            className={[
              'flex h-full w-full items-center justify-center uppercase text-white/40',
              compact ? 'text-[clamp(30px,3vw,56px)]' : 'text-[clamp(44px,5vw,88px)]',
            ].join(' ')}
          >
            ♪
          </div>
        ) : asset.category === 'animation' ? (
          <div
            className={[
              'flex h-full w-full items-center justify-center uppercase tracking-[0.18em] text-white/40',
              compact ? 'text-[clamp(11px,0.78vw,14px)]' : 'text-[clamp(14px,1vw,20px)]',
            ].join(' ')}
          >
            motion
          </div>
        ) : asset.reviewable ? (
          // Reviewable but no image landed (e.g. a non-image take) — genuinely
          // nothing to preview AND not actively generating → static.
          <div
            className={[
              'flex h-full w-full items-center justify-center uppercase text-white/40',
              compact ? 'text-[clamp(12px,0.85vw,16px)]' : 'text-[clamp(16px,1.1vw,22px)]',
            ].join(' ')}
            data-testid={`tv-steering-asset-no-preview-${asset.id}`}
          >
            no preview
          </div>
        ) : (
          // Still generating (not reviewable yet, no image) — animated shimmer
          // so the empty tile reads as ALIVE, not frozen.
          <div
            className="tv-steering-shimmer flex h-full w-full flex-col items-center justify-center gap-1.5"
            data-testid={`tv-steering-asset-generating-${asset.id}`}
            data-generating="true"
            role="status"
            aria-label={`Generating ${label}`}
          >
            <span
              className={[
                'animate-spin rounded-full border-2 border-white/15 border-t-crucible-accent',
                compact ? 'h-6 w-6' : 'h-8 w-8',
              ].join(' ')}
            />
            <span
              className={[
                'uppercase text-white/45',
                compact ? 'text-[clamp(11px,0.78vw,14px)]' : 'text-[clamp(14px,1vw,20px)]',
              ].join(' ')}
            >
              Generating…
            </span>
          </div>
        )}
      </div>

      <div className="flex min-w-0 items-center justify-between gap-3 px-0.5">
        <p
          className={[
            'truncate font-semibold text-white/90',
            compact ? 'text-[clamp(15px,1.1vw,20px)]' : 'text-[clamp(22px,1.65vw,32px)]',
          ].join(' ')}
        >
          {descriptor}
        </p>
        <span
          className={[
            'flex shrink-0 items-center gap-1.5 font-semibold uppercase tracking-[0.14em]',
            approved
              ? 'text-green-300/88'
              : pending
                ? 'text-crucible-accent'
                : busy
                  ? 'text-white/72'
                  : 'text-white/42',
            compact ? 'text-[clamp(9px,0.62vw,11px)]' : 'text-[clamp(12px,0.9vw,16px)]',
          ].join(' ')}
          // crucible#2783 — a pending approve owns the status testid over a
          // regenerate's `reshaping`, so a slow approve is queryable distinctly.
          data-testid={
            pending
              ? `tv-steering-asset-approving-${asset.id}`
              : busy
                ? `tv-steering-asset-reshaping-${asset.id}`
                : undefined
          }
          role={pending ? 'status' : undefined}
          aria-live={pending ? 'polite' : undefined}
        >
          {pending && (
            <span
              className={[
                'animate-spin rounded-full border-2 border-crucible-accent/40 border-t-crucible-accent',
                compact ? 'h-3 w-3' : 'h-3.5 w-3.5',
              ].join(' ')}
              aria-hidden="true"
            />
          )}
          {statusLabel}
        </span>
      </div>
      {asset.bgRemovalDefect && (
        <div
          className={[
            'rounded border border-red-300/40 bg-red-500/15 font-semibold text-red-100',
            compact ? 'px-2 py-1 text-[clamp(10px,0.7vw,12px)]' : 'px-3 py-1.5 text-sm',
          ].join(' ')}
          role="alert"
        >
          Background not removed
        </div>
      )}
    </div>
  );
}
